import { newestConfirmJob } from '@dealstack/shared/lib/dealstackAi/confirmation';
import { useCallback, useContext } from 'react';
import { TransactionSelectionContext } from '../components/Selection/TransactionSelectionProvider';
import {
	keepPreviousData,
	queryOptions,
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
	type QueryClient,
	type QueryKey,
} from '@tanstack/react-query';
import {
	DealstackAiConfirmationStatus,
	type ConfirmationRes,
} from '@dealstack/shared/lib/backendClient';
import {
	dealstackAiAcceleratorsControllerApplyDecision,
	dealstackAiAcceleratorsControllerConfirmCrossDocumentDateGroup,
	dealstackAiConfirmControllerConfirm,
	dealstackAiConfirmControllerGetConfirmation,
	dealstackAiConfirmControllerPreview,
	dealstackAiProvenanceControllerGetProvenance,
	dealstackAiResolveControllerAcceptSecurityItem,
	dealstackAiResolveControllerApplyCurrencySuggestion,
	dealstackAiResolveControllerResolveEntity,
	dealstackAiReviewWorkflowControllerClearDocument,
	dealstackAiReviewWorkflowControllerClearGroup,
	dealstackAiReviewWorkflowControllerClearRow,
	dealstackAiReviewWorkflowControllerEnterBulkCost,
	dealstackAiReviewWorkflowControllerEnterCost,
	dealstackAiReviewWorkflowControllerMarkDocument,
	dealstackAiReviewWorkflowControllerMarkGroup,
	dealstackAiReviewWorkflowControllerMarkRow,
	dealstackAiReviewWorkflowControllerSetParty,
	dealstackAiRowActionsControllerEnterDate,
	dealstackAiRowActionsControllerOverrideFx,
	dealstackAiRowActionsControllerRecordCorrection,
	dealstackAiRowActionsControllerRecordDecision,
} from '@dealstack/shared/lib/backendClient/sdk.gen';
import {
	dealstackAiAcceleratorsControllerListDateGroupsOptions,
	dealstackAiConfirmControllerGetConfirmationOptions,
	dealstackAiProvenanceControllerGetProvenanceQueryKey,
	dealstackAiReviewBatchControllerGetBatchOptions,
	dealstackAiReviewBatchControllerGetIssuersOptions,
	dealstackAiReviewBatchControllerGetOverviewOptions,
	dealstackAiReviewBatchControllerGetStatusBarOptions,
	dealstackAiReviewBatchControllerGetStatusBarQueryKey,
	dealstackAiReviewBatchControllerListBatchesOptions,
	dealstackAiReviewBatchControllerListBatchesQueryKey,
	dealstackAiReviewDocumentsControllerGetDocumentsSummaryOptions,
	dealstackAiReviewDocumentsControllerGetDocumentViewerOptions,
	dealstackAiReviewDocumentsControllerListDocumentsOptions,
	dealstackAiReviewEntitiesControllerGetEntityOptions,
	dealstackAiReviewEntitiesControllerGetResolveOptionsOptions,
	dealstackAiReviewEntitiesControllerListEntitiesOptions,
	dealstackAiReviewTransactionsControllerGetTransactionOptions,
	dealstackAiReviewTransactionsControllerListTransactionsOptions,
	dealstackAiReviewWorkflowControllerPartyOptionsOptions,
} from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { getPersonsForCap } from '@dealstack/frontend/app/services/Person.service';
import { getDealSecurities } from '@dealstack/frontend/shared/dataExchange/services/securities';
import {
	CALLER_HANDLES_CONFLICT,
	httpStatusOf,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { suppressMutationErrorToast } from '@dealstack/frontend/shared/dataExchange/reactQuery/queryErrorHandler';
import type {
	AcceptSecurityItemBody,
	ApplyDecisionBody,
	BatchParams,
	BulkCostBody,
	BulkDateBody,
	ConfirmDateGroupBody,
	ConfirmRowsBody,
	CostEntryBody,
	DocumentListFilter,
	DocumentViewerQuery,
	EntityListQuery,
	FieldCorrectionBody,
	FxOverrideBody,
	GroupRequestBody,
	InformationRequestBody,
	PartyCorrectionBody,
	RecordDecisionBody,
	ResolveEntityBody,
	TransactionListQuery,
} from '@dealstack/frontend/app/services/dealstackAi/dealstackAi.types';
import {
	answerOf,
	batchCall,
	batchItemCall,
	batchItemRequest,
	batchRequest,
	dealHeaders,
	issuerQuery,
	readsBatch,
	transactionsListHash,
} from './dealstackAiQueries.utils';
import { TRANSACTIONS_PAGE_INLINE_ERROR } from './transactionPageView';
import type { BatchWriteOptions } from './useDealstackAiQueries.types';

/**
 * The app's default `gcTime: 0` would drop a prefetched landing or a category switched back to. Results data
 * changes only through the mutations below, which invalidate the batch, so it can stay fresh for a while.
 */
const BATCH_CACHE = { gcTime: 5 * 60 * 1000 } as const;
export const RESULTS_CACHE = { ...BATCH_CACHE, staleTime: 30 * 1000 } as const;

/** How often a running confirm is polled. */
export const CONFIRMATION_POLL_MS = 2000;

/** Every review read of the batch, for one invalidation after a write. */
export const batchQueriesFilters = (batchId: string) => ({
	predicate: ({ queryKey }: { queryKey: QueryKey }) => readsBatch(queryKey, batchId),
});

/** Every deal's batch list. Built per call: the key carries the client's base URL, set at startup. */
export const batchListsFilters = () => ({
	queryKey: dealstackAiReviewBatchControllerListBatchesQueryKey(),
});

/** The batch's status bar under every issuer scope. */
export const statusBarQueriesFilters = (batchId: string) => ({
	queryKey: dealstackAiReviewBatchControllerGetStatusBarQueryKey({ path: { batchId } }),
});

const refreshBatch = (queryClient: QueryClient, batchId: string) =>
	Promise.all([
		queryClient.invalidateQueries(batchQueriesFilters(batchId)),
		queryClient.invalidateQueries(batchListsFilters()),
	]);

export const batchesQueryOptions = (dealId: string) =>
	queryOptions({
		...dealstackAiReviewBatchControllerListBatchesOptions({ headers: dealHeaders(dealId) }),
		...BATCH_CACHE,
	});

export const batchQueryOptions = (params: BatchParams) =>
	queryOptions({
		...dealstackAiReviewBatchControllerGetBatchOptions(batchRequest(params)),
		...BATCH_CACHE,
	});

export const overviewQueryOptions = (params: BatchParams) =>
	queryOptions({
		...dealstackAiReviewBatchControllerGetOverviewOptions({
			...batchRequest(params),
			...issuerQuery(params),
		}),
		...RESULTS_CACHE,
	});

export const statusBarQueryOptions = (params: BatchParams) =>
	queryOptions({
		...dealstackAiReviewBatchControllerGetStatusBarOptions({
			...batchRequest(params),
			...issuerQuery(params),
		}),
		...RESULTS_CACHE,
	});

export const documentsSummaryQueryOptions = (params: BatchParams) =>
	queryOptions({
		...dealstackAiReviewDocumentsControllerGetDocumentsSummaryOptions({
			...batchRequest(params),
			...issuerQuery(params),
		}),
		...RESULTS_CACHE,
	});

export const documentsQueryOptions = (params: BatchParams, filter: DocumentListFilter) =>
	queryOptions({
		...dealstackAiReviewDocumentsControllerListDocumentsOptions({
			...batchRequest(params),
			query:
				params.issuerId === undefined ? filter : { ...filter, issuerId: params.issuerId },
		}),
		...RESULTS_CACHE,
	});

export const entityListQueryOptions = (params: BatchParams, query: EntityListQuery) =>
	queryOptions({
		...dealstackAiReviewEntitiesControllerListEntitiesOptions({
			...batchRequest(params),
			query: params.issuerId === undefined ? query : { ...query, issuerId: params.issuerId },
		}),
		...RESULTS_CACHE,
	});

/**
 * One page of the transactions list; page 1 is requested without `page`. A later page is held for as long as the
 * table shows it (it is unobserved while scrolled away) and only a write refetches it, never time passing, so it stays
 * of a piece with page 1; a failure the table shows in place raises no error toast.
 */
export const transactionsPageQueryOptions = (
	params: BatchParams,
	query: TransactionListQuery,
	page: number
) => {
	const scoped = params.issuerId === undefined ? query : { ...query, issuerId: params.issuerId };
	return queryOptions({
		...dealstackAiReviewTransactionsControllerListTransactionsOptions({
			...batchRequest(params),
			query: page > 1 ? { ...scoped, page } : scoped,
		}),
		...RESULTS_CACHE,
		...(page > 1 && {
			gcTime: Number.POSITIVE_INFINITY,
			staleTime: Number.POSITIVE_INFINITY,
			meta: { inlineError: TRANSACTIONS_PAGE_INLINE_ERROR },
		}),
	});
};

/** Every loaded page of one transactions list. */
export const transactionsListFilters = (params: BatchParams, query: TransactionListQuery) => {
	const list = transactionsListHash(transactionsPageQueryOptions(params, query, 1).queryKey);
	return {
		predicate: ({ queryKey }: { queryKey: QueryKey }) =>
			transactionsListHash(queryKey) === list,
	};
};

export const dateGroupsQueryOptions = (params: BatchParams) =>
	queryOptions({
		...dealstackAiAcceleratorsControllerListDateGroupsOptions({
			...batchRequest(params),
			...issuerQuery(params),
		}),
		...RESULTS_CACHE,
	});

// Viewer, drawer and row-detail queries stay disabled while nothing is open (`null`).
const documentViewerQueryOptions = (
	params: BatchParams,
	documentId: string | null,
	query: DocumentViewerQuery
) =>
	queryOptions({
		...dealstackAiReviewDocumentsControllerGetDocumentViewerOptions({
			...batchItemRequest(params, { documentId: documentId ?? '' }),
			query,
		}),
		enabled: documentId !== null,
	});

const entityQueryOptions = (params: BatchParams, entityId: string | null) =>
	queryOptions({
		...dealstackAiReviewEntitiesControllerGetEntityOptions(
			batchItemRequest(params, { entityId: entityId ?? '' })
		),
		enabled: entityId !== null,
	});

const resolveOptionsQueryOptions = (params: BatchParams, entityId: string | null, search: string) =>
	queryOptions({
		...dealstackAiReviewEntitiesControllerGetResolveOptionsOptions({
			...batchItemRequest(params, { entityId: entityId ?? '' }),
			...(search ? { query: { search } } : {}),
		}),
		enabled: entityId !== null,
	});

const transactionQueryOptions = (params: BatchParams, transactionId: string | null) =>
	queryOptions({
		...dealstackAiReviewTransactionsControllerGetTransactionOptions(
			batchItemRequest(params, { rowId: transactionId ?? '' })
		),
		enabled: transactionId !== null,
		...RESULTS_CACHE,
	});

const confirmationQueryOptions = (params: BatchParams, confirmationId: string) =>
	dealstackAiConfirmControllerGetConfirmationOptions(
		batchItemRequest(params, { confirmationId })
	);

export const useAnalysisBatchesQuery = (dealId: string) => useQuery(batchesQueryOptions(dealId));

export const useAnalysisBatchQuery = (params: BatchParams) => useQuery(batchQueryOptions(params));

export const useBatchIssuersQuery = (params: BatchParams) =>
	useQuery({
		...dealstackAiReviewBatchControllerGetIssuersOptions(batchRequest(params)),
		...RESULTS_CACHE,
	});

export const useAnalysisOverviewQuery = (params: BatchParams) =>
	useQuery(overviewQueryOptions(params));

export const useStatusBarQuery = (params: BatchParams) => useQuery(statusBarQueryOptions(params));

export const useDocumentsSummaryQuery = (params: BatchParams) =>
	useQuery(documentsSummaryQueryOptions(params));

export const useDocumentsQuery = (params: BatchParams, filter: DocumentListFilter) =>
	useQuery(documentsQueryOptions(params, filter));

export const useDocumentViewerQuery = (
	params: BatchParams,
	documentId: string | null,
	query: DocumentViewerQuery = {}
) => useQuery(documentViewerQueryOptions(params, documentId, query));

export const useEntityListQuery = (params: BatchParams, query: EntityListQuery) =>
	useQuery(entityListQueryOptions(params, query));

export const useEntityQuery = (params: BatchParams, entityId: string | null) =>
	useQuery(entityQueryOptions(params, entityId));

export const useResolveOptionsQuery = (params: BatchParams, entityId: string | null, search = '') =>
	useQuery({
		...resolveOptionsQueryOptions(params, entityId, search),
		placeholderData: keepPreviousData,
	});

export const useTransactionQuery = (params: BatchParams, transactionId: string | null) =>
	useQuery(transactionQueryOptions(params, transactionId));

/** Rows named by id across the deal's batches, e.g. the other rows of a likely duplicate. */
export const useTransactionsByIdQueries = (
	dealId: string,
	refs: ReadonlyArray<{ batchId: string; transactionId: string }>
) =>
	useQueries({
		queries: refs.map(({ batchId, transactionId }) =>
			transactionQueryOptions({ dealId, batchId }, transactionId)
		),
	});

export const useDateGroupsQuery = (params: BatchParams) => useQuery(dateGroupsQueryOptions(params));

/** A confirm, polled while its job runs. */
export const useConfirmationQuery = (params: BatchParams, confirmationId: string | null) => {
	const client = useQueryClient();
	const options = confirmationQueryOptions(params, confirmationId ?? '');
	return useQuery({
		...options,
		queryFn: async ({ signal }): Promise<ConfirmationRes> => {
			const { data: next } = await dealstackAiConfirmControllerGetConfirmation({
				...batchItemCall(params, { confirmationId: confirmationId ?? '' }),
				signal,
			});
			const current = client.getQueryData<ConfirmationRes>(options.queryKey);
			if (next.status !== 'running' || !current) {
				return next;
			}
			if (current.status !== 'running') {
				return current;
			}
			return { ...next, job: newestConfirmJob(current.job, next.job) };
		},
		enabled: confirmationId !== null,
		retry: 2,
		retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
		refetchOnReconnect: true,
		refetchOnWindowFocus: true,
		refetchInterval: (query) =>
			!query.state.data || query.state.data.status === DealstackAiConfirmationStatus.RUNNING
				? CONFIRMATION_POLL_MS
				: false,
	});
};

/**
 * A write to the batch; on success it refreshes every query of the batch and the deal's batch list. A failure is the
 * app's error toast unless `callerReportsFailure`.
 */
export const useBatchWriteMutation = <TVariables, TData>(
	params: BatchParams,
	write: (variables: TVariables) => Promise<TData>,
	{ clearSelection = false, callerReportsFailure = false }: BatchWriteOptions = {}
) => {
	const queryClient = useQueryClient();
	const selection = useContext(TransactionSelectionContext);
	return useMutation({
		mutationFn: write,
		...(callerReportsFailure ? { onError: suppressMutationErrorToast } : {}),
		onSuccess: async () => {
			if (clearSelection) {
				selection?.clear();
			}
			await refreshBatch(queryClient, params.batchId);
		},
	});
};

export const useResolveEntityMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ entityId, request }: { entityId: string; request: ResolveEntityBody }) =>
			answerOf(
				dealstackAiResolveControllerResolveEntity({
					...batchItemCall(params, { entityId }),
					body: request,
				})
			),
		{ clearSelection: true }
	);

export const useApplyCurrencySuggestionMutation = (params: BatchParams) =>
	useBatchWriteMutation(params, (entityId: string) =>
		answerOf(
			dealstackAiResolveControllerApplyCurrencySuggestion(batchItemCall(params, { entityId }))
		)
	);

export const useAcceptSecurityItemMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ entityId, body }: { entityId: string; body?: AcceptSecurityItemBody }) =>
			answerOf(
				dealstackAiResolveControllerAcceptSecurityItem({
					...batchItemCall(params, { entityId }),
					body,
				})
			),
		{ clearSelection: true }
	);

export const useRecordDecisionMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ transactionId, body }: { transactionId: string; body: RecordDecisionBody }) =>
			answerOf(
				dealstackAiRowActionsControllerRecordDecision({
					...batchItemCall(params, { rowId: transactionId }),
					body,
				})
			),
		{ callerReportsFailure: true }
	);

export const useRecordCorrectionMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ transactionId, body }: { transactionId: string; body: FieldCorrectionBody }) =>
			answerOf(
				dealstackAiRowActionsControllerRecordCorrection({
					...batchItemCall(params, { rowId: transactionId }),
					body,
				})
			)
	);

export const useOverrideFxMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ transactionId, body }: { transactionId: string; body: FxOverrideBody }) =>
			answerOf(
				dealstackAiRowActionsControllerOverrideFx({
					...batchItemCall(params, { rowId: transactionId }),
					body,
				})
			)
	);

export const useBulkDateMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		(body: BulkDateBody) =>
			answerOf(dealstackAiRowActionsControllerEnterDate({ ...batchCall(params), body })),
		{ callerReportsFailure: true }
	);

export const useApplyDecisionMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		(body: ApplyDecisionBody) =>
			answerOf(
				dealstackAiAcceleratorsControllerApplyDecision({ ...batchCall(params), body })
			),
		{ callerReportsFailure: true }
	);

export const useConfirmCrossDocumentDateGroupMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ groupKey, body }: { groupKey: string; body: ConfirmDateGroupBody }) =>
			answerOf(
				dealstackAiAcceleratorsControllerConfirmCrossDocumentDateGroup({
					...batchItemCall(params, { groupKey }),
					body,
				})
			),
		{ callerReportsFailure: true }
	);

/** The party picker's people; disabled while no role is being edited. */
export const usePartyOptionsQuery = (params: BatchParams, search: string, enabled: boolean) =>
	useQuery({
		...dealstackAiReviewWorkflowControllerPartyOptionsOptions({
			...batchRequest(params),
			...(search ? { query: { search } } : {}),
		}),
		enabled,
		placeholderData: keepPreviousData,
		...BATCH_CACHE,
	});

export const useEnterRowCostMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ transactionId, body }: { transactionId: string; body: CostEntryBody }) =>
			answerOf(
				dealstackAiReviewWorkflowControllerEnterCost({
					...batchItemCall(params, { rowId: transactionId }),
					body,
				})
			)
	);

export const useBulkCostMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		(body: BulkCostBody) =>
			answerOf(
				dealstackAiReviewWorkflowControllerEnterBulkCost({ ...batchCall(params), body })
			),
		{ callerReportsFailure: true }
	);

export const useSetRowPartyMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ transactionId, body }: { transactionId: string; body: PartyCorrectionBody }) =>
			answerOf(
				dealstackAiReviewWorkflowControllerSetParty({
					...batchItemCall(params, { rowId: transactionId }),
					body,
				})
			),
		{ callerReportsFailure: true }
	);

export const useMarkRowInformationRequestMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ transactionId, body }: { transactionId: string; body: InformationRequestBody }) =>
			answerOf(
				dealstackAiReviewWorkflowControllerMarkRow({
					...batchItemCall(params, { rowId: transactionId }),
					body,
				})
			)
	);

export const useClearRowInformationRequestMutation = (params: BatchParams) =>
	useBatchWriteMutation(params, ({ transactionId }: { transactionId: string }) =>
		answerOf(
			dealstackAiReviewWorkflowControllerClearRow(
				batchItemCall(params, { rowId: transactionId })
			)
		)
	);

export const useMarkDocumentInformationRequestMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		({ documentId, body }: { documentId: string; body: InformationRequestBody }) =>
			answerOf(
				dealstackAiReviewWorkflowControllerMarkDocument({
					...batchItemCall(params, { documentId }),
					body,
				})
			)
	);

export const useClearDocumentInformationRequestMutation = (params: BatchParams) =>
	useBatchWriteMutation(params, ({ documentId }: { documentId: string }) =>
		answerOf(
			dealstackAiReviewWorkflowControllerClearDocument(batchItemCall(params, { documentId }))
		)
	);

export const useMarkGroupInformationRequestMutation = (params: BatchParams) =>
	useBatchWriteMutation(
		params,
		(body: GroupRequestBody) =>
			answerOf(dealstackAiReviewWorkflowControllerMarkGroup({ ...batchCall(params), body })),
		{ callerReportsFailure: true }
	);

export const useClearGroupInformationRequestMutation = (params: BatchParams) =>
	useBatchWriteMutation(params, ({ requestId }: { requestId: string }) =>
		answerOf(
			dealstackAiReviewWorkflowControllerClearGroup(batchItemCall(params, { requestId }))
		)
	);

/** The read-only preview; the confirm modal shows its 409 itself. */
export const usePreviewConfirmMutation = (params: BatchParams) =>
	useMutation({
		mutationFn: (body: ConfirmRowsBody) =>
			answerOf(dealstackAiConfirmControllerPreview({ ...batchCall(params), body })),
		meta: CALLER_HANDLES_CONFLICT,
	});

/** Starts the confirm job; the rows it takes turn `confirming` at once. Its 409s are shown by the confirm modal. */
export const useConfirmRowsMutation = (params: BatchParams) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: ConfirmRowsBody) =>
			answerOf(dealstackAiConfirmControllerConfirm({ ...batchCall(params), body })),
		meta: CALLER_HANDLES_CONFLICT,
		onSuccess: (confirmation) => {
			queryClient.setQueryData(
				confirmationQueryOptions(params, confirmation.id).queryKey,
				confirmation
			);
			void refreshBatch(queryClient, params.batchId);
		},
	});
};

/** Refreshes every query of the batch and the deal's batch list, e.g. once a polled confirm settles. */
export const useRefreshBatchQueries = (params: BatchParams) => {
	const queryClient = useQueryClient();
	return useCallback(
		() => refreshBatch(queryClient, params.batchId),
		[queryClient, params.batchId]
	);
};

/** The deal's persons: a security create's issuer, and the name of a security item's issuer. */
export const useDealPeopleQuery = (dealId: string) =>
	useQuery({
		queryKey: [API_PATHS.PERSON_DEAL_DEALID_PERSONS_FOR_CAP_GET, { dealId }],
		queryFn: () =>
			getPersonsForCap(API_PATHS.PERSON_DEAL_DEALID_PERSONS_FOR_CAP_GET, { dealId }),
		...BATCH_CACHE,
	});

/** The deal's securities: a placed item's security, and a tracking share's tracked class. */
export const useDealSecuritiesQuery = (dealId: string) =>
	useQuery({
		queryKey: [API_PATHS.SECURITY_DEAL_GET, { dealId }],
		queryFn: () => getDealSecurities(API_PATHS.SECURITY_DEAL_GET),
		...BATCH_CACHE,
	});

/**
 * The provenance line of a cap-table transaction: null for one this pipeline did not write, which the route answers
 * with a 404. The line is optional, so a failed read never toasts.
 */
export const useWrittenTransactionProvenanceQuery = (
	dealId: string,
	transactionId: string | null
) => {
	const request = { headers: dealHeaders(dealId), path: { transactionId: transactionId ?? '' } };
	return useQuery({
		queryKey: [...dealstackAiProvenanceControllerGetProvenanceQueryKey(request), 'or-null'],
		queryFn: async ({ signal }) => {
			try {
				return await answerOf(
					dealstackAiProvenanceControllerGetProvenance({
						...request,
						signal,
						throwOnError: true,
					})
				);
			} catch (error) {
				if (httpStatusOf(error) === 404) {
					return null;
				}
				throw error;
			}
		},
		enabled: transactionId !== null,
		meta: { suppressErrorToast: true },
	});
};

/** A batch read outside its workspace, e.g. a written transaction's batch; disabled while `batchId` is null. */
export const useOptionalAnalysisBatchQuery = (dealId: string, batchId: string | null) =>
	useQuery({
		...batchQueryOptions({ dealId, batchId: batchId ?? '' }),
		enabled: batchId !== null,
		meta: { suppressErrorToast: true },
	});
